import { z } from "zod";

/**
 * Zod schema defining analyzable fields for enduser (patient) records.
 *
 * This schema serves two purposes:
 * 1. Validates MongoDB aggregation pipeline field references
 * 2. Auto-generates field documentation for LLM context via Zod's describe()
 *
 * Fields are described to help the LLM understand what data is available
 * and how to construct meaningful aggregation queries.
 */
export const enduserAnalyticsSchema = z.object({
  // use the internal _id field --> very useful for analytics queries, client-facing "id" version is not helpful
  _id: z.string().describe("Unique identifier for the patient"),
  updatedAt: z.date().describe("When the patient record was last updated"),
  fname: z.string().describe("Patient's first name"),
  lname: z.string().describe("Patient's last name"),
  email: z.string().describe("Patient's email address"),
  phone: z.string().describe("Patient's phone number"),
  dateOfBirth: z.date().describe("Patient's date of birth"),
  gender: z.string().describe("Patient's gender (male, female, other, prefer-not-to-say)"),

  // Address fields
  addressLineOne: z.string().describe("Street address line 1"),
  addressLineTwo: z.string().optional().describe("Street address line 2 (optional)"),
  city: z.string().describe("City"),
  state: z.string().describe("State or province"),
  zipCode: z.string().describe("ZIP or postal code"),

  // Healthcare-specific fields
  tags: z.array(z.string()).describe("Tags/categories assigned to this patient"),
  assignedTo: z.array(z.string()).describe("Array of user IDs this patient is assigned to"),

  // Metadata
  source: z.string().optional().describe("How the patient was added to the system"),
  externalId: z.string().optional().describe("External system identifier"),
});

export type EnduserAnalyticsFields = z.infer<typeof enduserAnalyticsSchema>;
